// Turns a GeneratedLevel into Three.js meshes.
//
// Every tile is a solid prism extruded from its top surface down to y=0 (like the
// source .obj's stacked Roblox parts). Flat tiles merge per elevation level into one
// mesh; ramps are per-tile sloped prisms; curbs are low rails around exposed edges so
// the corridor shape and drops read clearly. Color encodes elevation.

import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import {
  CELL,
  CURB_H,
  CURB_T,
  CURB_COLOR,
  RAMP_COLOR,
  DECK_THICKNESS,
  levelColor,
  topY,
} from "../config";
import { DVEC, type GeneratedLevel, type Tile, type CellInfo } from "./grammar";
import { computeCurbs, rampCornerHeights, rampSurfaceHeight } from "./geometry";

export interface MeshOptions {
  curbs: boolean;
  /** Far-off cosmetic sprawl: cast no shadows (off-frustum + adds nothing readable, so it skips the
   *  extra shadow-map draws). */
  decorative?: boolean;
}

/** A solid prism: axis-aligned footprint with four independently-heighted top corners. */
function buildPrism(
  x0: number,
  x1: number,
  z0: number,
  z1: number,
  hA: number,
  hB: number,
  hC: number,
  hD: number,
): THREE.BufferGeometry {
  // A(x0,z0) B(x1,z0) C(x1,z1) D(x0,z1) — top; *b — bottom at y=0.
  const A = [x0, hA, z0];
  const B = [x1, hB, z0];
  const C = [x1, hC, z1];
  const D = [x0, hD, z1];
  const Ab = [x0, 0, z0];
  const Bb = [x1, 0, z0];
  const Cb = [x1, 0, z1];
  const Db = [x0, 0, z1];
  const tris = [
    [A, C, B],
    [A, D, C], // top
    [Ab, Bb, Cb],
    [Ab, Cb, Db], // bottom
    [A, Bb, Ab],
    [A, B, Bb], // z0 wall
    [B, Cb, Bb],
    [B, C, Cb], // x1 wall
    [C, Db, Cb],
    [C, D, Db], // z1 wall
    [D, Ab, Db],
    [D, A, Ab], // x0 wall
  ];
  const pos: number[] = [];
  for (const t of tris) for (const v of t) pos.push(v[0], v[1], v[2]);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return geo;
}

function makeMat(color: THREE.Color | number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.96,
    metalness: 0.0,
    flatShading: true,
  });
}

/** A thin wall from a bottom quad up to a +CURB_H top (position+normal only, to merge with prisms). */
function box8(b: number[][], t: number[][]): THREE.BufferGeometry {
  const tris = [
    [b[0], b[1], b[2]], [b[0], b[2], b[3]],
    [t[0], t[2], t[1]], [t[0], t[3], t[2]],
    [b[0], t[1], t[0]], [b[0], b[1], t[1]],
    [b[1], t[2], t[1]], [b[1], b[2], t[2]],
    [b[2], t[3], t[2]], [b[2], b[3], t[3]],
    [b[3], t[0], t[3]], [b[3], b[0], t[0]],
  ];
  const pos: number[] = [];
  for (const tr of tris) for (const v of tr) pos.push(v[0], v[1], v[2]);
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

/** Sloped rails along a ramp's EXPOSED sides (neighbor empty) so a player can't walk off the
 *  slope. The rail top follows the ramp surface — curbs finally get applied to slopes. */
function rampSideRails(t: Tile, cells: Map<string, CellInfo>): THREE.BufferGeometry[] {
  const geoms: THREE.BufferGeometry[] = [];
  const surf = (x: number, z: number): number => rampSurfaceHeight(t, x, z);
  const cx0 = Math.round(t.x0 / CELL);
  const cx1 = Math.round(t.x1 / CELL);
  const cz0 = Math.round(t.z0 / CELL);
  const cz1 = Math.round(t.z1 / CELL);
  const travelZ = t.ramp!.travelDir === 0 || t.ramp!.travelDir === 2;
  const railZ = (xe: number, za: number, zb: number, inward: number): void => {
    const x0 = Math.min(xe, xe + inward * CURB_T);
    const x1 = Math.max(xe, xe + inward * CURB_T);
    const ha = surf(xe, za);
    const hb = surf(xe, zb);
    const b = [[x0, ha, za], [x1, ha, za], [x1, hb, zb], [x0, hb, zb]];
    geoms.push(box8(b, b.map((p) => [p[0], p[1] + CURB_H, p[2]])));
  };
  const railX = (ze: number, xa: number, xb: number, inward: number): void => {
    const z0 = Math.min(ze, ze + inward * CURB_T);
    const z1 = Math.max(ze, ze + inward * CURB_T);
    const ha = surf(xa, ze);
    const hb = surf(xb, ze);
    const b = [[xa, ha, z0], [xa, ha, z1], [xb, hb, z1], [xb, hb, z0]];
    geoms.push(box8(b, b.map((p) => [p[0], p[1] + CURB_H, p[2]])));
  };
  if (travelZ) {
    for (let cz = cz0; cz < cz1; cz++) {
      const za = cz * CELL;
      const zb = (cz + 1) * CELL;
      if (!cells.has(`${cx0 - 1},${cz}`)) railZ(t.x0, za, zb, +1);
      if (!cells.has(`${cx1},${cz}`)) railZ(t.x1, za, zb, -1);
    }
  } else {
    for (let cx = cx0; cx < cx1; cx++) {
      const xa = cx * CELL;
      const xb = (cx + 1) * CELL;
      if (!cells.has(`${cx},${cz0 - 1}`)) railX(t.z0, xa, xb, +1);
      if (!cells.has(`${cx},${cz1}`)) railX(t.z1, xa, xb, -1);
    }
  }
  return geoms;
}

/** Build the full mesh group for a level. */
export function buildLevelMesh(lvl: GeneratedLevel, opts: MeshOptions): THREE.Group {
  const group = new THREE.Group();
  group.name = "level";
  const cast = !opts.decorative; // decorative sprawl casts no shadows (off-frustum + adds nothing readable)

  // ---- floors, merged per elevation level (bridges are thin floating decks) ----
  const byLevel = new Map<number, THREE.BufferGeometry[]>();
  const bridgeByLevel = new Map<number, THREE.BufferGeometry[]>();
  const ramps: THREE.BufferGeometry[] = [];
  const push = (m: Map<number, THREE.BufferGeometry[]>, lvl_: number, g: THREE.BufferGeometry): void => {
    const list = m.get(lvl_) ?? [];
    list.push(g);
    m.set(lvl_, list);
  };
  for (const t of lvl.tiles) {
    if (t.kind === "ramp") {
      const [hA, hB, hC, hD] = rampCornerHeights(t);
      ramps.push(buildPrism(t.x0, t.x1, t.z0, t.z1, hA, hB, hC, hD));
    } else if (t.kind === "bridge") {
      const h = topY(t.level);
      const g = new THREE.BoxGeometry(t.x1 - t.x0, DECK_THICKNESS, t.z1 - t.z0);
      g.translate((t.x0 + t.x1) / 2, h - DECK_THICKNESS / 2, (t.z0 + t.z1) / 2);
      push(bridgeByLevel, t.level, g);
    } else {
      const h = topY(t.level);
      push(byLevel, t.level, buildPrism(t.x0, t.x1, t.z0, t.z1, h, h, h, h));
    }
  }

  for (const [level, geoms] of byLevel) {
    const merged = mergeGeometries(geoms, false);
    for (const g of geoms) g.dispose();
    const mesh = new THREE.Mesh(merged, makeMat(levelColor(level)));
    mesh.castShadow = cast;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  for (const [level, geoms] of bridgeByLevel) {
    const merged = mergeGeometries(geoms, false);
    for (const g of geoms) g.dispose();
    const mesh = new THREE.Mesh(merged, makeMat(levelColor(level)));
    mesh.castShadow = cast;
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  if (ramps.length) {
    const merged = mergeGeometries(ramps, false);
    for (const g of ramps) g.dispose();
    const mesh = new THREE.Mesh(merged, makeMat(RAMP_COLOR));
    mesh.castShadow = cast;
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  // ---- curbs: flat perimeter/drop rails (shared with the solvability checker) + sloped
  //      rails along exposed ramp sides. All position+normal only so they merge together. ----
  if (opts.curbs) {
    const curbGeoms: THREE.BufferGeometry[] = [];
    for (const p of computeCurbs(lvl.cells).placements) {
      const [dx, dz] = DVEC[p.dir];
      const baseY = topY(p.level);
      let g: THREE.BufferGeometry;
      if (dx !== 0) {
        g = new THREE.BoxGeometry(CURB_T, CURB_H, CELL);
        g.translate((p.cx + (dx > 0 ? 1 : 0)) * CELL, baseY + CURB_H / 2, (p.cz + 0.5) * CELL);
      } else {
        g = new THREE.BoxGeometry(CELL, CURB_H, CURB_T);
        g.translate((p.cx + 0.5) * CELL, baseY + CURB_H / 2, (p.cz + (dz > 0 ? 1 : 0)) * CELL);
      }
      g.deleteAttribute("uv"); // match the position+normal-only ramp rails so they merge
      const ni = g.toNonIndexed(); // ramp rails are non-indexed; make these match
      g.dispose();
      curbGeoms.push(ni);
    }
    for (const t of lvl.tiles) {
      if (t.kind === "ramp") curbGeoms.push(...rampSideRails(t, lvl.cells));
    }
    if (curbGeoms.length) {
      const merged = mergeGeometries(curbGeoms, false);
      for (const g of curbGeoms) g.dispose();
      const mat = makeMat(CURB_COLOR);
      mat.side = THREE.DoubleSide; // thin rails: avoid any back-face culling on the sloped walls
      const mesh = new THREE.Mesh(merged, mat);
      mesh.castShadow = false; // 52-stud rails cast only noise against the floor — skip the depth pass
      group.add(mesh);
    }
  }

  // start marker + enemy-core objective removed — base-building replaces the "reach the core" objective

  return group;
}


/** Recursively dispose a level group's geometries + materials. */
export function disposeGroup(group: THREE.Group): void {
  group.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.geometry.dispose();
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      for (const m of mats) {
        // free any per-material textures held in ShaderMaterial uniforms (e.g. the terra heightmap) —
        // material.dispose() alone doesn't touch uniform textures, so they'd leak on every rebuild
        const u = (m as THREE.ShaderMaterial).uniforms;
        if (u) for (const k in u) { const v = u[k]?.value; if (v instanceof THREE.Texture) v.dispose(); }
        // free a render target held in userData (e.g. the water's planar-reflection RT + its framebuffer)
        const rt = (m.userData as { rt?: THREE.WebGLRenderTarget }).rt;
        if (rt && typeof rt.dispose === "function") rt.dispose();
        m.dispose();
      }
    }
  });
}
